// src/storage/explorerPoints.ts — Effort-Based Explorer Points & Specimen Stamps (FR-EFFORT-001, FR-MAP-002)
// Rewards perseverance, hint utilization, harder tier attempts, and mistake correction over raw speed.

export type EffortEventType = 'hint_then_correct' | 'harder_tier' | 'mistake_corrected';

export const EP_VALUES: Record<EffortEventType, number> = {
  hint_then_correct: 3, // +3 EP: Guided thinking followed by success
  harder_tier: 2,       // +2 EP: Bravery tackling Hard or Challenge tier
  mistake_corrected: 5, // +5 EP: Growth mindset correcting a past stumble
};

/** Speed alone awards ZERO bonus points (FR-EFFORT-001) */
export const SPEED_BONUS_EP = 0;

export interface EffortAward {
  type: EffortEventType;
  points: number;
  label: string;
  description: string;
  questionId?: string;
  timestamp: number;
}

export interface SpecimenStamp {
  id: string;
  name: string;
  habitat: string;
  icon: string;
  epThreshold: number;
  unlocked: boolean;
  unlockedAt?: number;
}

export const INITIAL_SPECIMEN_STAMPS: SpecimenStamp[] = [
  { id: 'ammonite', name: 'Pyrite Ammonite Fossil', habitat: 'The Shallows', icon: '🐚', epThreshold: 0, unlocked: true },
  { id: 'sea_urchin', name: 'Spiny Sea Urchin', habitat: 'Tide Pools', icon: '🦔', epThreshold: 15, unlocked: false },
  { id: 'nautilus', name: 'Gilded Nautilus Chamber', habitat: 'Coral Reef', icon: '🪸', epThreshold: 35, unlocked: false },
  { id: 'lobster', name: 'European Lobster', habitat: 'Rocky Ledge', icon: '🦞', epThreshold: 60, unlocked: false },
  { id: 'lanternfish', name: 'Bioluminescent Lanternfish', habitat: 'Twilight Depths', icon: '🐟', epThreshold: 95, unlocked: false },
  { id: 'sea_lily', name: 'Crinoid Sea Lily', habitat: 'Benthic Plain', icon: '🌿', epThreshold: 135, unlocked: false },
  { id: 'giant_squid', name: 'Abyssal Giant Squid', habitat: 'Midnight Trench', icon: '🦑', epThreshold: 180, unlocked: false },
];

export interface ExplorerPointsData {
  version: number;
  totalEp: number;
  breakdown: {
    hint_then_correct: number;
    harder_tier: number;
    mistake_corrected: number;
  };
  eventLog: EffortAward[];
  unlockedStampIds: string[];
}

const STORAGE_KEY = 'clawey_explorer_points_v1';

export class ExplorerPointsManager {
  static getDefault(): ExplorerPointsData {
    return {
      version: 1,
      totalEp: 0,
      breakdown: {
        hint_then_correct: 0,
        harder_tier: 0,
        mistake_corrected: 0,
      },
      eventLog: [],
      unlockedStampIds: ['ammonite'],
    };
  }

  static load(): ExplorerPointsData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return this.getDefault();
      return { ...this.getDefault(), ...JSON.parse(raw) };
    } catch {
      return this.getDefault();
    }
  }

  static save(data: ExplorerPointsData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore quota errors in constrained environments
    }
  }

  static clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  static getTotalEp(): number {
    return this.load().totalEp;
  }

  static getBreakdown(): Record<EffortEventType, number> {
    return this.load().breakdown;
  }

  /**
   * Returns list of all specimen stamps with their unlock status based on persisted data.
   */
  static getStamps(): SpecimenStamp[] {
    const data = this.load();
    return INITIAL_SPECIMEN_STAMPS.map((stamp) => ({
      ...stamp,
      unlocked: data.unlockedStampIds.includes(stamp.id) || data.totalEp >= stamp.epThreshold,
    }));
  }

  /**
   * Returns list of only currently unlocked specimen stamps.
   */
  static getUnlockedStamps(): SpecimenStamp[] {
    return this.getStamps().filter((s) => s.unlocked);
  }

  /**
   * Awards Explorer Points for a qualifying effort event (FR-EFFORT-001).
   * Checks whether cumulative EP unlocks new Specimen Stamps (FR-MAP-002).
   */
  static award(
    type: EffortEventType,
    questionId?: string
  ): {
    epAwarded: number;
    newTotal: number;
    award: EffortAward;
    newStampsUnlocked: SpecimenStamp[];
  } {
    const data = this.load();
    const points = EP_VALUES[type] ?? 0;

    let label = 'Effort Explorer';
    let description = 'Perseverance recognized!';

    if (type === 'hint_then_correct') {
      label = '💡 Clawey Nudge Victory';
      description = 'Carefully studied Clawey\'s clue and conquered the question (+3 EP)';
    } else if (type === 'harder_tier') {
      label = '🌊 Bravery in Deep Waters';
      description = 'Courageously tackled a Hard or Challenge tier question (+2 EP)';
    } else if (type === 'mistake_corrected') {
      label = '🔄 Master of the Second Dive';
      description = 'Overcame a previous mistake through true perseverance (+5 EP)';
    }

    const award: EffortAward = {
      type,
      points,
      label,
      description,
      questionId,
      timestamp: Date.now(),
    };

    data.totalEp += points;
    data.breakdown[type] = (data.breakdown[type] || 0) + points;
    data.eventLog.push(award);

    // Check for newly unlocked specimen stamps
    const newlyUnlocked: SpecimenStamp[] = [];
    for (const stamp of INITIAL_SPECIMEN_STAMPS) {
      if (!data.unlockedStampIds.includes(stamp.id) && data.totalEp >= stamp.epThreshold) {
        data.unlockedStampIds.push(stamp.id);
        newlyUnlocked.push({ ...stamp, unlocked: true, unlockedAt: Date.now() });
      }
    }

    this.save(data);

    return {
      epAwarded: points,
      newTotal: data.totalEp,
      award,
      newStampsUnlocked: newlyUnlocked,
    };
  }

  /**
   * Evaluates questions in a finished session and awards qualifying Explorer Points.
   * Speed alone contributes 0 EP.
   */
  static evaluateSession(attempts: Array<{
    questionId: string;
    usedHint: boolean;
    isCorrect: boolean;
    tier: 'easy' | 'medium' | 'hard' | 'challenge';
    wasPreviousMistake: boolean;
    durationMs: number;
  }>): {
    sessionEp: number;
    awards: EffortAward[];
    newStampsUnlocked: SpecimenStamp[];
  } {
    let sessionEp = 0;
    const sessionAwards: EffortAward[] = [];
    const allNewlyUnlocked: SpecimenStamp[] = [];

    for (const attempt of attempts) {
      // 1. Hint-then-correct (+3 EP)
      if (attempt.usedHint && attempt.isCorrect) {
        const res = this.award('hint_then_correct', attempt.questionId);
        sessionEp += res.epAwarded;
        sessionAwards.push(res.award);
        allNewlyUnlocked.push(...res.newStampsUnlocked);
      }

      // 2. Harder tier attempted (+2 EP)
      if (attempt.tier === 'hard' || attempt.tier === 'challenge') {
        const res = this.award('harder_tier', attempt.questionId);
        sessionEp += res.epAwarded;
        sessionAwards.push(res.award);
        allNewlyUnlocked.push(...res.newStampsUnlocked);
      }

      // 3. Mistake corrected (+5 EP)
      if (attempt.wasPreviousMistake && attempt.isCorrect) {
        const res = this.award('mistake_corrected', attempt.questionId);
        sessionEp += res.epAwarded;
        sessionAwards.push(res.award);
        allNewlyUnlocked.push(...res.newStampsUnlocked);
      }

      // Explicit: Speed awards 0 EP (FR-EFFORT-001)
    }

    return {
      sessionEp,
      awards: sessionAwards,
      newStampsUnlocked: allNewlyUnlocked,
    };
  }
}
